import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { persist } from "zustand/middleware";
import { useShallow } from "zustand/shallow";
import { useSyncExternalStore } from "react";

import { createAuthSlice, AuthSlice } from "./globalStoreSlices/authSlice";
import {
  createUserChoicesV2Slice,
  normalizePersistedUserChoicesState,
  pickPersistedUserChoicesState,
  UserChoiceV2State,
} from "./globalStoreSlices/userChoices";
import {
  createUserProfileSlice,
  UserProfileSlice,
} from "./globalStoreSlices/profileSlice";
import {
  createRagSlice,
  pickPersistedRagState,
  RagSlice,
} from "./globalStoreSlices/ragSlice";

export type GlobalState = AuthSlice &
  UserChoiceV2State &
  UserProfileSlice &
  RagSlice;

export const useGlobalStore = create<GlobalState>()(
  devtools(
    persist(
      (...a) => ({
        ...createAuthSlice(...a),
        ...createUserChoicesV2Slice(...a),
        ...createUserProfileSlice(...a),
        ...createRagSlice(...a),
      }),
      {
        // Name for the localStorage key
        name: "moveon-storage",
        partialize: (state) => ({
          ...pickPersistedUserChoicesState(state),
          ...pickPersistedRagState(state),
        }),
        merge: (persistedState, currentState) => {
          const persistedObject = (persistedState ?? {}) as Partial<GlobalState>;
          const normalizedPersistedState = normalizePersistedUserChoicesState(
            persistedObject,
          );

          return {
            ...currentState,
            ...persistedObject,
            ...normalizedPersistedState,
          };
        },
      },
    ),
    { name: "global-store" },
  ),
);

// --------------------------------------------------------------------
// Topic-specific hook: Auth slice
// --------------------------------------------------------------------

export const useAuth = () =>
  useGlobalStore(
    useShallow((state) => ({
      user: state.user,
      accessToken: state.accessToken,
      refreshToken: state.refreshToken,
      meiliUserToken: state.meiliUserToken,
      meiliUserTokenExpiresAt: state.meiliUserTokenExpiresAt,
      isInitialized: state.isInitialized,
      isUserAdmin: state.isUserAdmin,
      setUser: state.setUser,
      setTokens: state.setTokens,
      logout: state.logout,
    })),
  );

// --------------------------------------------------------------------
// Topic-specific hooks: User Choices and related
// --------------------------------------------------------------------
//
// Compatibility hook. New code should prefer the narrower hooks below when
// possible to avoid turning state/* into a hidden app-level service locator.
export const useUserChoices = () =>
  useGlobalStore(
    useShallow((state) => ({
      //////* Generic controllers
      interimState: state.interimState,
      setInterimState: state.setInterimState,
      resetInterimState: state.resetInterimState,

      //////* Switches */
      // Studio Prototype Category
      activePrototypeCategory: state.activePrototypeCategory,
      setActivePrototypeCategory: state.setActivePrototypeCategory,
      // Studio Grouping
      activeStudioGrouping: state.activeStudioGrouping,
      setActiveStudioGrouping: state.setActiveStudioGrouping,
      // Studio Content
      activeStudioContent: state.activeStudioContent,
      setActiveStudioContent: state.setActiveStudioContent,
      // Studio Export Lock
      activeStudioExportLock: state.activeStudioExportLock,
      setActiveStudioExportLock: state.setActiveStudioExportLock,
      toggleActiveStudioExportLock: state.toggleActiveStudioExportLock,
      canLockExport: state.canLockExport,
      // Studio Maintenance Mode
      activeStudioMaintenanceMode: state.activeStudioMaintenanceMode,
      setActiveStudioMaintenanceMode: state.setActiveStudioMaintenanceMode,
      activeProfileVersion: state.activeProfileVersion,
      setActiveProfileVersion: state.setActiveProfileVersion,
      clearActiveProfileVersion: state.clearActiveProfileVersion,
      syncActiveProfileVersion: state.syncActiveProfileVersion,
      // Backlog View(=Focus old naming)
      activeBacklogFocus: state.activeBacklogView,
      setActiveBacklogFocus: state.setActiveBacklogView,

      //////* UI Preferences */
      isSidebarCollapsed: state.isSidebarCollapsed,
      setIsSidebarCollapsed: state.setIsSidebarCollapsed,
      toggleSidebar: state.toggleSidebar,
      studioSituationLoadState: state.studioSituationLoadState,
      setStudioSituationLoaded: state.setStudioSituationLoaded,
      setStudioSituationsLoaded: state.setStudioSituationsLoaded,
      resetStudioSituationLoadState: state.resetStudioSituationLoadState,
      normalizeStudioSituationLoadState:
        state.normalizeStudioSituationLoadState,
      isStudioSituationLoaded: state.isStudioSituationLoaded,

      //////* Component Refresh Trigger */
      meiliRefreshStamp: state.meiliRefreshStamp,
      incrementMeiliRefreshStamp: state.incrementMeiliRefreshStamp,
      uiRefreshStamp: state.uiRefreshStamp,
      incrementUiRefreshStamp: state.incrementUiRefreshStamp,

      //////* Search Parameters  */
      semanticRatio: state.semanticRatio,
      setSemanticRatio: state.setSemanticRatio,
      resetSemanticRatio: state.resetSemanticRatio,
      targetsToRerank: state.targetsToRerank,
      setTargetsToRerank: state.setTargetsToRerank,
      resetTargetsToRerank: state.resetTargetsToRerank,
      //////* Questionnaire Parameters  */
      questionnaireViewModes: state.questionnaireViewModes,
      setQuestionnaireViewModes: state.setQuestionnaireViewModes,

      //////* Selected Situations  */
      selectedSituations: state.selectedSituations,
      addSelectedSituation: state.addSelectedSituation,
      getSelectedSituationIds: state.getSelectedSituationIds,
      isSelectedSituation: state.isSelectedSituation,
      removeSelectedSituation: state.removeSelectedSituation,
      toggleSelectedSituation: state.toggleSelectedSituation,
      removeSelectedSituations: state.removeSelectedSituations,
      countSelectedSituations: state.countSelectedSituations,
      saveSelectedSituationsSnapshot: state.saveSelectedSituationsSnapshot,
      hasSelectedSituationsChanged: state.hasSelectedSituationsChanged,
      resetSelectedSituations: state.resetSelectedSituations,

      //////* Selected Thoughts  */
      selectedThoughts: state.selectedThoughts,
      selectThought: state.selectThought,
      getSelectedThoughtsByPrototype: state.getSelectedThoughtsByPrototype,
      getSelectedThoughtsBySituationId: state.getSelectedThoughtsBySituationId,
      getSelectedThoughts: state.getSelectedThoughts,
      getSelectedThoughtById: state.getSelectedThoughtById,
      getSelectedThoughtIds: state.getSelectedThoughtIds,
      isSelectedThought: state.isSelectedThought,
      toggleThoughtSelection: state.toggleThoughtSelection,
      toggleAllSelectedThoughts: state.toggleAllSelectedThoughts,
      removeSelectedThoughts: state.removeSelectedThoughts,
      unselectThought: state.unselectThought,
      unselectThoughts: state.unselectThoughts,
      reassignSelectedThoughts: state.reassignSelectedThoughts,
      reorderSituationThoughtSelection: state.reorderSituationThoughtSelection,
      countSelectedThoughts: state.countSelectedThoughts,
      saveSelectedProfileSelectionSnapshot:
        state.saveSelectedProfileSelectionSnapshot,
      hasSelectedProfileSelectionChanged:
        state.hasSelectedProfileSelectionChanged,
      resetSelectedThoughts: state.resetSelectedThoughts,
      selectedThoughtsFree: state.selectedThoughtsFree,
      getSelectedThoughtsFree: state.getSelectedThoughtsFree,
      reorderFreeThoughtSelection: state.reorderFreeThoughtSelection,

      //////* Profile selection status (for convenience) */
      resetSelectedProfile: state.resetSelectedProfile,
      isSelectedProfile: state.isSelectedProfile(),
      applySelectedProfileSelection: state.applySelectedProfileSelection,

      //////* Selected Suggested Thoughts  */
      selectedSuggestedThoughts: state.selectedSuggestedThoughts,
      addSelectedSuggestedThought: state.addSelectedSuggestedThought,
      addSelectedSuggestedThoughts: state.addSelectedSuggestedThoughts,
      toggleSelectedSuggestedThought: state.toggleSelectedSuggestedThought,
      toggleAllSelectedSuggestedThoughts:
        state.toggleAllSelectedSuggestedThoughts,
      isSelectedSuggestedThought: state.isSelectedSuggestedThought,
      removeSelectedSuggestedThoughts: state.removeSelectedSuggestedThoughts,
      countSelectedSuggestedThoughts: state.countSelectedSuggestedThoughts,
      resetSelectedSuggestedThoughts: state.resetSelectedSuggestedThoughts,
      //////* Selected Origins  */
      selectedOrigins: state.selectedOrigins,
      addSelectedOrigin: state.addSelectedOrigin,
      toggleSelectedOrigin: state.toggleSelectedOrigin,
      isSelectedOrigin: state.isSelectedOrigin,
      removeSelectedOrigins: state.removeSelectedOrigins,
      countSelectedOrigins: state.countSelectedOrigins,
      getSelectedOriginIds: state.getSelectedOriginIds,
      resetSelectedOrigins: state.resetSelectedOrigins,
    })),
  );

export const useGlobalStoreHydrated = () =>
  useSyncExternalStore(
    (listener) => {
      const unsubscribeHydrate = useGlobalStore.persist.onHydrate(() => {
        listener();
      });
      const unsubscribeFinishHydration =
        useGlobalStore.persist.onFinishHydration(() => {
          listener();
        });

      return () => {
        unsubscribeHydrate();
        unsubscribeFinishHydration();
      };
    },
    () => useGlobalStore.persist.hasHydrated(),
    () => false,
  );

// Deprecated legacy slice kept in `globalStoreSlices/originSlice.ts` for
// reference only. It is intentionally no longer wired into the live store.

// --------------------------------------------------------------------
// Topic-specific hook: Situation slice
// --------------------------------------------------------------------

// --------------------------------------------------------------------
// Topic-specific hook: User Profile slice
// --------------------------------------------------------------------

export const useUserProfile = () =>
  useGlobalStore(
    useShallow((state) => ({
      userProfile: state.userProfile,
      setUserProfile: state.setUserProfile,
      setUserProfileLoading: state.setUserProfileLoading,
      setUserProfileError: state.setUserProfileError,
      resetUserProfile: state.resetUserProfile,
      updateUserProfilePersonalData: state.updateUserProfilePersonalData,
      updateUserProfilePrintSettings: state.updateUserProfilePrintSettings,
      // Initial state loading
      initFetchedUserProfile: state.initFetchedUserProfile,
      setInitFetchedUserProfile: state.setInitFetchedUserProfile,
    })),
  );

// --------------------------------------------------------------------
// Topic-specific hook: RAG slice
// --------------------------------------------------------------------

export const useRag = () =>
  useGlobalStore(
    useShallow((state) => ({
      isChatInitialized: state.isChatInitialized,
      chatMessages: state.chatMessages,
      setChatMessages: state.setChatMessages,
      addChatMessage: state.addChatMessage,
      updateChatMessage: state.updateChatMessage,
      deleteChatMessage: state.deleteChatMessage,
      addChatReaction: state.addChatReaction,
      resetChat: state.resetChat,
      // Selection management
      selectedChatMessages: state.selectedChatMessages,
      addSelectedChatMessage: state.addSelectedChatMessage,
      removeSelectedChatMessage: state.removeSelectedChatMessage,
      clearSelectedChatMessages: state.clearSelectedChatMessages,
      toggleSelectedChatMessage: state.toggleSelectedChatMessage,
      countSelectedChatMessages: state.countSelectedChatMessages,
      // Session management
      chatSessionId: state.chatSessionId,
      setChatSessionId: state.setChatSessionId,
      // Mode management
      chatMode: state.chatMode,
      setChatMode: state.setChatMode,
      // Loading states
      waitingForBot: state.waitingForBot,
      setWaitingForBot: state.setWaitingForBot,
      loadingKnowledge: state.loadingKnowledge,
      setLoadingKnowledge: state.setLoadingKnowledge,
      clearingRAG: state.clearingRAG,
      setClearingRAG: state.setClearingRAG,
      preparingChat: state.preparingChat,
      setPreparingChat: state.setPreparingChat,
    })),
  );

export const useShellViewState = () =>
  useGlobalStore(
    useShallow((state) => ({
      activePrototypeCategory: state.activePrototypeCategory,
      setActivePrototypeCategory: state.setActivePrototypeCategory,
      activeStudioGrouping: state.activeStudioGrouping,
      setActiveStudioGrouping: state.setActiveStudioGrouping,
      activeStudioContent: state.activeStudioContent,
      setActiveStudioContent: state.setActiveStudioContent,
      activeStudioExportLock: state.activeStudioExportLock,
      setActiveStudioExportLock: state.setActiveStudioExportLock,
      toggleActiveStudioExportLock: state.toggleActiveStudioExportLock,
      canLockExport: state.canLockExport,
      activeStudioMaintenanceMode: state.activeStudioMaintenanceMode,
      setActiveStudioMaintenanceMode: state.setActiveStudioMaintenanceMode,
      activeProfileVersion: state.activeProfileVersion,
      setActiveProfileVersion: state.setActiveProfileVersion,
      clearActiveProfileVersion: state.clearActiveProfileVersion,
      syncActiveProfileVersion: state.syncActiveProfileVersion,
      activeBacklogFocus: state.activeBacklogView,
      setActiveBacklogFocus: state.setActiveBacklogView,
    })),
  );

export const useSidebarPreferences = () =>
  useGlobalStore(
    useShallow((state) => ({
      isSidebarCollapsed: state.isSidebarCollapsed,
      setIsSidebarCollapsed: state.setIsSidebarCollapsed,
      toggleSidebar: state.toggleSidebar,
    })),
  );

export const useThoughtArrangementPreferences = () =>
  useGlobalStore(
    useShallow((state) => ({
      thoughtArrangementManualOverrides:
        state.thoughtArrangementManualOverrides,
      setThoughtArrangementManualOverride:
        state.setThoughtArrangementManualOverride,
      resetThoughtArrangementManualOverrides:
        state.resetThoughtArrangementManualOverrides,
    })),
  );

export const useMeiliRefreshState = () =>
  useGlobalStore(
    useShallow((state) => ({
      meiliRefreshStamp: state.meiliRefreshStamp,
      incrementMeiliRefreshStamp: state.incrementMeiliRefreshStamp,
    })),
  );

export const useUiRefreshState = () =>
  useGlobalStore(
    useShallow((state) => ({
      uiRefreshStamp: state.uiRefreshStamp,
      incrementUiRefreshStamp: state.incrementUiRefreshStamp,
    })),
  );

export const useTargetRerankState = () =>
  useGlobalStore(
    useShallow((state) => ({
      targetsToRerank: state.targetsToRerank,
      setTargetsToRerank: state.setTargetsToRerank,
      resetTargetsToRerank: state.resetTargetsToRerank,
    })),
  );

export const useQuestionnaireViewState = () =>
  useGlobalStore(
    useShallow((state) => ({
      questionnaireViewModes: state.questionnaireViewModes,
      setQuestionnaireViewModes: state.setQuestionnaireViewModes,
    })),
  );

export const useSuggestedThoughtSelection = () =>
  useGlobalStore(
    useShallow((state) => ({
      selectedSuggestedThoughts: state.selectedSuggestedThoughts,
      addSelectedSuggestedThought: state.addSelectedSuggestedThought,
      addSelectedSuggestedThoughts: state.addSelectedSuggestedThoughts,
      toggleSelectedSuggestedThought: state.toggleSelectedSuggestedThought,
      toggleAllSelectedSuggestedThoughts:
        state.toggleAllSelectedSuggestedThoughts,
      isSelectedSuggestedThought: state.isSelectedSuggestedThought,
      removeSelectedSuggestedThoughts: state.removeSelectedSuggestedThoughts,
      countSelectedSuggestedThoughts: state.countSelectedSuggestedThoughts,
      resetSelectedSuggestedThoughts: state.resetSelectedSuggestedThoughts,
    })),
  );

export const useOriginSelection = () =>
  useGlobalStore(
    useShallow((state) => ({
      selectedOrigins: state.selectedOrigins,
      addSelectedOrigin: state.addSelectedOrigin,
      toggleSelectedOrigin: state.toggleSelectedOrigin,
      isSelectedOrigin: state.isSelectedOrigin,
      removeSelectedOrigins: state.removeSelectedOrigins,
      countSelectedOrigins: state.countSelectedOrigins,
      getSelectedOriginIds: state.getSelectedOriginIds,
      resetSelectedOrigins: state.resetSelectedOrigins,
    })),
  );

export const useChatSessionState = () =>
  useGlobalStore(
    useShallow((state) => ({
      isChatInitialized: state.isChatInitialized,
      chatMessages: state.chatMessages,
      setChatMessages: state.setChatMessages,
      addChatMessage: state.addChatMessage,
      updateChatMessage: state.updateChatMessage,
      deleteChatMessage: state.deleteChatMessage,
      addChatReaction: state.addChatReaction,
      resetChat: state.resetChat,
      selectedChatMessages: state.selectedChatMessages,
      addSelectedChatMessage: state.addSelectedChatMessage,
      removeSelectedChatMessage: state.removeSelectedChatMessage,
      clearSelectedChatMessages: state.clearSelectedChatMessages,
      toggleSelectedChatMessage: state.toggleSelectedChatMessage,
      countSelectedChatMessages: state.countSelectedChatMessages,
      chatSessionId: state.chatSessionId,
      setChatSessionId: state.setChatSessionId,
      chatMode: state.chatMode,
      setChatMode: state.setChatMode,
    })),
  );

export const useChatLoadingState = () =>
  useGlobalStore(
    useShallow((state) => ({
      waitingForBot: state.waitingForBot,
      setWaitingForBot: state.setWaitingForBot,
      loadingKnowledge: state.loadingKnowledge,
      setLoadingKnowledge: state.setLoadingKnowledge,
      clearingRAG: state.clearingRAG,
      setClearingRAG: state.setClearingRAG,
      preparingChat: state.preparingChat,
      setPreparingChat: state.setPreparingChat,
    })),
  );

// --------------------------------------------------------------------
// Selective hooks (prevenring over-subscriptions)
// --------------------------------------------------------------------

// Selection state manager (for selected thoughts/situations)
export const useSelectionState = () =>
  useGlobalStore(
    useShallow((state) => ({
      selectedSituations: state.selectedSituations,
      selectedThoughts: state.selectedThoughts,
      selectedThoughtsFree: state.selectedThoughtsFree,
      selectedSuggestedThoughts: state.selectedSuggestedThoughts,
      countSelectedThoughts: state.countSelectedThoughts,
      countSelectedSituations: state.countSelectedSituations,
      countSelectedSuggestedThoughts: state.countSelectedSuggestedThoughts,
      getSelectedThoughtIds: state.getSelectedThoughtIds,
      getSelectedSituationIds: state.getSelectedSituationIds,
      resetSelectedSuggestedThoughts: state.resetSelectedSuggestedThoughts,
    })),
  );

// Interim state manager (for temporary states that do not need full persistence)
export const useInterimStateManager = () =>
  useGlobalStore(
    useShallow((state) => ({
      interimState: state.interimState,
      setInterimState: state.setInterimState,
      resetInterimState: state.resetInterimState,
    })),
  );

// Optimized selector hooks for search queries Select View (prevents unnecessary re-renders)
export const useSearchQuerySelect = () =>
  useGlobalStore(
    useShallow((state) => ({
      searchQuery: state.searchQuerySelect,
      setSearchQuery: state.setSearchQuerySelect,
      resetSearchQuery: state.resetSearchQuerySelect,
    })),
  );

// Optimized selector hooks for search queries Check View (prevents unnecessary re-renders)
export const useSearchQueryCheck = () =>
  useGlobalStore(
    useShallow((state) => ({
      searchQuery: state.searchQueryCheck,
      setSearchQuery: state.setSearchQueryCheck,
      resetSearchQuery: state.resetSearchQueryCheck,
    })),
  );

// Optimized selector hooks for backlog search query
export const useSearchQueryBacklog = () =>
  useGlobalStore(
    useShallow((state) => ({
      searchQuery: state.searchQueryBacklog,
      setSearchQuery: state.setSearchQueryBacklog,
      resetSearchQuery: state.resetSearchQueryBacklog,
    })),
  );

// Backlog search UI controls
export const useBacklogSearchControls = () =>
  useGlobalStore(
    useShallow((state) => ({
      hitsPerPage: state.backlogHitsPerPage,
      setHitsPerPage: state.setBacklogHitsPerPage,
      sortBy: state.backlogSortBy,
      setSortBy: state.setBacklogSortBy,
      semanticRatio: state.backlogSemanticRatio,
      setSemanticRatio: state.setBacklogSemanticRatio,
      filters: state.backlogFilters,
      setFilters: state.setBacklogFilters,
      resetSearchAdjustments: state.resetBacklogSearchAdjustments,
    })),
  );

// Studio select search UI controls
export const useSelectSearchControls = () =>
  useGlobalStore(
    useShallow((state) => ({
      hitsPerPage: state.selectHitsPerPage,
      setHitsPerPage: state.setSelectHitsPerPage,
      sortBy: state.selectSortBy,
      setSortBy: state.setSelectSortBy,
      semanticRatio: state.selectSemanticRatio,
      setSemanticRatio: state.setSelectSemanticRatio,
      filters: state.selectFilters,
      setFilters: state.setSelectFilters,
      resetSearchAdjustments: state.resetSelectSearchAdjustments,
    })),
  );

// Studio check search UI controls
export const useCheckSearchControls = () =>
  useGlobalStore(
    useShallow((state) => ({
      hitsPerPage: state.checkHitsPerPage,
      setHitsPerPage: state.setCheckHitsPerPage,
      sortBy: state.checkSortBy,
      setSortBy: state.setCheckSortBy,
      semanticRatio: state.checkSemanticRatio,
      setSemanticRatio: state.setCheckSemanticRatio,
      filters: state.checkFilters,
      setFilters: state.setCheckFilters,
      resetSearchAdjustments: state.resetCheckSearchAdjustments,
    })),
  );
